import * as React from "react";
import { ReactElement, useCallback, useEffect, useMemo } from "react";
import { UseQueryResult } from "react-query";
import { useClusterMetrics } from "../../../../crud/metrics/hooks";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { getLatestTimestamp } from "../utils";
import { GroupedMetric } from "../../../../crud/metrics/types";
import { ZoomableAreaChartProps } from "./ZoomableAreaChart";
import { ZoomableAreaChartWithQuery } from "./ZoomableAreaChartWithQuery";
import { Variation } from "./types";

// Shadcn Select components
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";

type ZoomableAreaChartWithVariationsProps = Omit<
  ZoomableAreaChartProps,
  "data" | "domains" | "isLoading" | "isError"
> & {
  clusterId: string;
  instanceIds?: number[];
  variations: Variation[];
  title: string;
  chartKey?: string;
};

/**
 * A zoomable area chart that allows the user to select from a list of variations
 * and automatically fetches the appropriate data.
 */
export const ZoomableAreaChartWithVariations = (
  props: ZoomableAreaChartWithVariationsProps,
): ReactElement => {
  const variationKey = `${
    props.chartKey || props.title.toLowerCase()
  }_variation`;
  // use local storage for variation preference if not explicitly set in URL
  const defaultVariationValue =
    localStorage.getItem(variationKey) || props.variations?.[0]?.name;

  const [selectedVariationName, setSelectedVariationName] = useQueryParam(
    variationKey,
    withDefault(StringParam, defaultVariationValue),
  );
  const selectedVariation =
    props.variations.find(
      (v) => v.name === (selectedVariationName || defaultVariationValue),
    ) || props.variations?.[0];

  const domains = selectedVariation?.domains || [];
  const yAxis = selectedVariation?.yAxis || props.yAxis;
  const legend = selectedVariation?.legend || props.legend;

  const query = useClusterMetrics(
    props.clusterId,
    selectedVariation.groupKey,
    selectedVariation.queries,
    props.instanceIds,
    selectedVariation.name,
    selectedVariation?.domains,
    props.defaultTimeRange,
  );

  const fetchNextPage = useCallback(() => {
    const latestTimestamp = getLatestTimestamp(query.data?.pages.slice(-10));
    if (latestTimestamp || props.defaultTimeRange) {
      query.fetchNextPage({
        pageParam:
          latestTimestamp || Math.floor(props.defaultTimeRange![0] / 1000),
      });
    }
    // Only include stable query.fetchNextPage in dependencies to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.fetchNextPage]);

  // Fetch new data when defaultTimeRange updates
  useEffect(() => {
    if (props.defaultTimeRange && props.clusterIsRunning) {
      fetchNextPage();
    }
  }, [props.defaultTimeRange, props.clusterIsRunning, fetchNextPage]);

  const metricsQuery = useMemo(
    () =>
      ({
        ...query,
        data: query.data?.pages?.flatMap((p) => p.data) || [],
      }) as UseQueryResult<GroupedMetric[]>,
    // Only include stable query.data?.pages in dependencies to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data?.pages],
  );

  const handleChange = (value: string) => {
    localStorage.setItem(variationKey, value);
    setSelectedVariationName(value);
  };

  const title =
    props.variations?.length > 1 ? (
      <div className="flex items-center gap-3">
        <div className="text-lg font-medium">{props.title}</div>
        <Select value={selectedVariation?.name} onValueChange={handleChange}>
          <SelectTrigger className="py-0 pr-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {props.variations.map(({ name }) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : (
      <>{props.title}</>
    );

  return (
    <ZoomableAreaChartWithQuery
      {...props}
      legend={legend}
      yAxis={yAxis}
      title={title}
      domains={domains}
      metricsQuery={metricsQuery}
    />
  );
};
